import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  message,
  Popconfirm,
  Tabs,
  Checkbox,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

// 会员黑名单接口（整合活动选择）
interface MemberBlacklist {
  id: number;
  memberId: number;
  memberAccount: string;
  depositWithdrawalCount?: string; // 充/提次数
  depositWithdrawalAmount?: string; // 充/提金额
  depositWithdrawalDifference?: number; // 充/提差额
  totalBalance?: number; // 总余额
  registrationIp?: string; // 注册IP
  restrictionTypes: string[]; // 限制类型：注册、登录、游戏、奖励、充值、提现、登出
  activityIds?: number[]; // 活动ID数组（如果有限制奖励）
  activityNames?: string[]; // 活动名称
  remarks: string; // 备注
  operator: string; // 操作人
  createTime: string;
  updateTime: string;
}

// IP黑名单接口
interface IpBlacklist {
  id: number;
  ipAddress: string;
  restrictionType: string; // 限制类型：登录、注册、提现、游戏等
  remarks: string;
  operator: string;
  createTime: string;
  updateTime: string;
}

// 手机黑名单接口
interface PhoneBlacklist {
  id: number;
  phoneNumber: string;
  restrictionType: string;
  remarks: string;
  operator: string;
  createTime: string;
  updateTime: string;
}

// 邮箱黑名单接口
interface EmailBlacklist {
  id: number;
  email: string;
  restrictionType: string;
  remarks: string;
  operator: string;
  createTime: string;
  updateTime: string;
}

// 设备黑名单接口
interface DeviceBlacklist {
  id: number;
  device: string;
  restrictionType: string;
  remarks: string;
  operator: string;
  createTime: string;
  updateTime: string;
}

// 支付账号黑名单接口
interface PaymentAccountBlacklist {
  id: number;
  paymentAccount: string;
  restrictionType: string;
  remarks: string;
  operator: string;
  createTime: string;
  updateTime: string;
}

// 拦截明细接口
interface InterceptDetail {
  id: number;
  userId: number;
  userAccount: string;
  interceptType: 'reward' | 'game' | 'withdrawal' | 'login' | 'register' | 'recharge';
  interceptTypeName: string;
  activityId?: number;
  activityName?: string;
  reason: string;
  interceptTime: string;
  blacklistId: number;
}

// 模拟活动数据
const mockActivities = [
  { id: 1, name: '首充活动', code: 'ACT_FIRST_RECHARGE' },
  { id: 2, name: '每日充值', code: 'ACT_DAILY_RECHARGE' },
  { id: 3, name: '邀请好友', code: 'ACT_INVITE_FRIEND' },
  { id: 4, name: 'VIP活动', code: 'ACT_VIP' },
  { id: 5, name: '幸运转盘', code: 'ACT_LUCKY_WHEEL' },
  { id: 6, name: '每周亏损', code: 'ACT_WEEKLY_LOSS' },
  { id: 7, name: '投注返现', code: 'ACT_BET_REBATE' },
  { id: 8, name: '体育亏损返现', code: 'ACT_SPORTS_LOSS_REBATE' },
];

// 限制类型选项
const restrictionTypeOptions = [
  { value: 'register', label: '注册' },
  { value: 'login', label: '登录' },
  { value: 'game', label: '游戏' },
  { value: 'reward', label: '奖励' },
  { value: 'recharge', label: '充值' },
  { value: 'withdrawal', label: '提现' },
  { value: 'logout', label: '登出' },
];

// 生成随机IP
const generateRandomIp = () => {
  return `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`;
};

// 生成随机手机号
const generateRandomPhone = () => {
  return `84${Math.floor(Math.random() * 9000000000) + 1000000000}`;
};

// 生成随机邮箱
const generateRandomEmail = () => {
  const domains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'qq.com'];
  const randomStr = Math.random().toString(36).substring(7);
  return `${randomStr}@${domains[Math.floor(Math.random() * domains.length)]}`;
};

// 生成随机设备ID
const generateRandomDevice = () => {
  return Math.random().toString(36).substring(2, 18) + Math.random().toString(36).substring(2, 18);
};

// 生成随机支付账号
const generateRandomPaymentAccount = () => {
  const prefixes = ['VISA', 'MASTER', 'PAYPAL', 'ALIPAY', 'WECHAT'];
  return `${prefixes[Math.floor(Math.random() * prefixes.length)]}${Math.floor(Math.random() * 1000000000000000)}`;
};

// 模拟会员黑名单数据
const generateMemberBlacklistData = (): MemberBlacklist[] => {
  const data: MemberBlacklist[] = [];
  for (let i = 1; i <= 50; i++) {
    const restrictionTypes = restrictionTypeOptions
      .sort(() => Math.random() - 0.5)
      .slice(0, Math.floor(Math.random() * 4) + 1)
      .map(opt => opt.value);
    
    const hasReward = restrictionTypes.includes('reward');
    const activityIds = hasReward
      ? mockActivities
          .sort(() => Math.random() - 0.5)
          .slice(0, Math.floor(Math.random() * 3) + 1)
          .map(a => a.id)
      : undefined;
    const activityNames = hasReward
      ? mockActivities.filter(a => activityIds?.includes(a.id)).map(a => a.name)
      : undefined;

    data.push({
      id: 258714 + i,
      memberId: 258714 + i,
      memberAccount: `Test${4622 + i}`,
      depositWithdrawalCount: Math.random() > 0.5 ? `${Math.floor(Math.random() * 10)}/${Math.floor(Math.random() * 10)}` : '/',
      depositWithdrawalAmount: Math.random() > 0.5 ? `${Math.floor(Math.random() * 100000)}/${Math.floor(Math.random() * 100000)}` : '/',
      depositWithdrawalDifference: Math.floor(Math.random() * 100000),
      totalBalance: Math.random() * 1000000,
      registrationIp: Math.random() > 0.3 ? generateRandomIp() : undefined,
      restrictionTypes,
      activityIds,
      activityNames,
      remarks: `member.relation.info.ban PH ONE_DEVICE_ID`,
      operator: 'admin',
      createTime: dayjs().subtract(Math.floor(Math.random() * 30), 'day').format('YYYY-MM-DD HH:mm:ss'),
      updateTime: dayjs().subtract(Math.floor(Math.random() * 30), 'day').format('YYYY-MM-DD HH:mm:ss'),
    });
  }
  return data;
};

// 模拟IP黑名单数据
const generateIpBlacklistData = (): IpBlacklist[] => {
  const data: IpBlacklist[] = [];
  const ips = ['104.28.205.70', '104.28.222.73', '104.28.223.45', '104.28.224.56'];
  const types = ['login', 'register', 'withdrawal', 'game'];
  const remarks = ['刷子', '1', '禁止注册'];
  const operators = ['hxhx', 'daaaz', 'admin'];

  for (let i = 1; i <= 42; i++) {
    data.push({
      id: 83664 - i,
      ipAddress: i <= 4 ? ips[i - 1] : generateRandomIp(),
      restrictionType: types[Math.floor(Math.random() * types.length)],
      remarks: remarks[Math.floor(Math.random() * remarks.length)],
      operator: operators[Math.floor(Math.random() * operators.length)],
      createTime: dayjs().subtract(Math.floor(Math.random() * 200), 'day').format('YYYY-MM-DD HH:mm:ss'),
      updateTime: dayjs().subtract(Math.floor(Math.random() * 200), 'day').format('YYYY-MM-DD HH:mm:ss'),
    });
  }
  return data;
};

// 模拟手机黑名单数据
const generatePhoneBlacklistData = (): PhoneBlacklist[] => {
  const data: PhoneBlacklist[] = [];
  for (let i = 1; i <= 10; i++) {
    data.push({
      id: 83825 + i,
      phoneNumber: generateRandomPhone(),
      restrictionType: restrictionTypeOptions[Math.floor(Math.random() * restrictionTypeOptions.length)].value,
      remarks: '1',
      operator: 'daaaz',
      createTime: dayjs().subtract(Math.floor(Math.random() * 100), 'day').format('YYYY-MM-DD HH:mm:ss'),
      updateTime: dayjs().subtract(Math.floor(Math.random() * 100), 'day').format('YYYY-MM-DD HH:mm:ss'),
    });
  }
  return data;
};

// 模拟邮箱黑名单数据
const generateEmailBlacklistData = (): EmailBlacklist[] => {
  const data: EmailBlacklist[] = [];
  for (let i = 1; i <= 20; i++) {
    data.push({
      id: 1000 + i,
      email: generateRandomEmail(),
      restrictionType: restrictionTypeOptions[Math.floor(Math.random() * restrictionTypeOptions.length)].value,
      remarks: '风险邮箱',
      operator: 'admin',
      createTime: dayjs().subtract(Math.floor(Math.random() * 50), 'day').format('YYYY-MM-DD HH:mm:ss'),
      updateTime: dayjs().subtract(Math.floor(Math.random() * 50), 'day').format('YYYY-MM-DD HH:mm:ss'),
    });
  }
  return data;
};

// 模拟设备黑名单数据
const generateDeviceBlacklistData = (): DeviceBlacklist[] => {
  const data: DeviceBlacklist[] = [];
  const devices = [
    '83e43879d8ebf7c1',
    'b648553724b485ca',
    'a603a1ce28a2e96c',
    '03A94E2F-7BEA-4522-9F93-B90A2AA1BC8B',
    '287166b1ecd3c1f2',
  ];
  const types = ['login', 'register'];
  const remarks = ['刷子', '1'];
  const operators = ['hxhx', 'admin'];

  for (let i = 1; i <= 226; i++) {
    data.push({
      id: 80097 - i,
      device: i <= 5 ? devices[i - 1] : generateRandomDevice(),
      restrictionType: types[Math.floor(Math.random() * types.length)],
      remarks: remarks[Math.floor(Math.random() * remarks.length)],
      operator: operators[Math.floor(Math.random() * operators.length)],
      createTime: dayjs().subtract(Math.floor(Math.random() * 300), 'day').format('YYYY-MM-DD HH:mm:ss'),
      updateTime: dayjs().subtract(Math.floor(Math.random() * 300), 'day').format('YYYY-MM-DD HH:mm:ss'),
    });
  }
  return data;
};

// 模拟支付账号黑名单数据
const generatePaymentAccountBlacklistData = (): PaymentAccountBlacklist[] => {
  const data: PaymentAccountBlacklist[] = [];
  for (let i = 1; i <= 15; i++) {
    data.push({
      id: 5000 + i,
      paymentAccount: generateRandomPaymentAccount(),
      restrictionType: restrictionTypeOptions[Math.floor(Math.random() * restrictionTypeOptions.length)].value,
      remarks: '风险账号',
      operator: 'admin',
      createTime: dayjs().subtract(Math.floor(Math.random() * 30), 'day').format('YYYY-MM-DD HH:mm:ss'),
      updateTime: dayjs().subtract(Math.floor(Math.random() * 30), 'day').format('YYYY-MM-DD HH:mm:ss'),
    });
  }
  return data;
};

// 模拟拦截明细数据
const generateInterceptDetails = (): InterceptDetail[] => {
  const data: InterceptDetail[] = [];
  const types: Array<{ type: InterceptDetail['interceptType']; name: string }> = [
    { type: 'reward', name: '奖励派发' },
    { type: 'game', name: '进游戏' },
    { type: 'withdrawal', name: '提现' },
    { type: 'login', name: '登录' },
    { type: 'register', name: '注册' },
    { type: 'recharge', name: '充值' },
  ];

  for (let i = 1; i <= 100; i++) {
    const interceptType = types[Math.floor(Math.random() * types.length)];
    const hasActivity = interceptType.type === 'reward';
    
    data.push({
      id: i,
      userId: 258714 + Math.floor(Math.random() * 50),
      userAccount: `Test${4622 + Math.floor(Math.random() * 50)}`,
      interceptType: interceptType.type,
      interceptTypeName: interceptType.name,
      activityId: hasActivity ? mockActivities[Math.floor(Math.random() * mockActivities.length)].id : undefined,
      activityName: hasActivity ? mockActivities[Math.floor(Math.random() * mockActivities.length)].name : undefined,
      reason: hasActivity
        ? '用户被加入黑名单，无法参与该活动'
        : `用户被限制${interceptType.name}`,
      interceptTime: dayjs().subtract(Math.floor(Math.random() * 7), 'day').format('YYYY-MM-DD HH:mm:ss'),
      blacklistId: 258714 + Math.floor(Math.random() * 10),
    });
  }
  return data;
};

const BlacklistManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState<string>('member');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);

  // 数据状态
  const [memberBlacklistData, setMemberBlacklistData] = useState<MemberBlacklist[]>(generateMemberBlacklistData());
  const [ipBlacklistData, setIpBlacklistData] = useState<IpBlacklist[]>(generateIpBlacklistData());
  const [phoneBlacklistData, setPhoneBlacklistData] = useState<PhoneBlacklist[]>(generatePhoneBlacklistData());
  const [emailBlacklistData, setEmailBlacklistData] = useState<EmailBlacklist[]>(generateEmailBlacklistData());
  const [deviceBlacklistData, setDeviceBlacklistData] = useState<DeviceBlacklist[]>(generateDeviceBlacklistData());
  const [paymentAccountBlacklistData, setPaymentAccountBlacklistData] = useState<PaymentAccountBlacklist[]>(generatePaymentAccountBlacklistData());
  const [interceptDetails] = useState<InterceptDetail[]>(generateInterceptDetails());

  // 获取当前tab的数据
  const getCurrentData = () => {
    switch (activeTab) {
      case 'member':
        return memberBlacklistData;
      case 'ip':
        return ipBlacklistData;
      case 'phone':
        return phoneBlacklistData;
      case 'email':
        return emailBlacklistData;
      case 'device':
        return deviceBlacklistData;
      case 'payment':
        return paymentAccountBlacklistData;
      default:
        return [];
    }
  };

  // 过滤数据
  const getFilteredData = () => {
    const data = getCurrentData();
    let filtered: any[] = [...data];

    if (searchText) {
      if (activeTab === 'member') {
        filtered = (filtered as MemberBlacklist[]).filter((item: MemberBlacklist) =>
          item.memberAccount.toLowerCase().includes(searchText.toLowerCase()) ||
          item.memberId.toString().includes(searchText)
        );
      } else if (activeTab === 'ip') {
        filtered = (filtered as IpBlacklist[]).filter((item: IpBlacklist) =>
          item.ipAddress.includes(searchText)
        );
      } else if (activeTab === 'phone') {
        filtered = (filtered as PhoneBlacklist[]).filter((item: PhoneBlacklist) =>
          item.phoneNumber.includes(searchText)
        );
      } else if (activeTab === 'email') {
        filtered = (filtered as EmailBlacklist[]).filter((item: EmailBlacklist) =>
          item.email.toLowerCase().includes(searchText.toLowerCase())
        );
      } else if (activeTab === 'device') {
        filtered = (filtered as DeviceBlacklist[]).filter((item: DeviceBlacklist) =>
          item.device.toLowerCase().includes(searchText.toLowerCase())
        );
      } else if (activeTab === 'payment') {
        filtered = (filtered as PaymentAccountBlacklist[]).filter((item: PaymentAccountBlacklist) =>
          item.paymentAccount.toLowerCase().includes(searchText.toLowerCase())
        );
      }
    }

    if (filterType !== 'all') {
      if (activeTab === 'member') {
        filtered = (filtered as MemberBlacklist[]).filter((item: MemberBlacklist) =>
          item.restrictionTypes.includes(filterType)
        );
      } else {
        filtered = filtered.filter((item: any) => item.restrictionType === filterType);
      }
    }

    return filtered;
  };

  // 处理打开新增/编辑弹窗
  const handleOpenModal = (record?: any) => {
    if (record) {
      setEditingRecord(record);
      if (activeTab === 'member') {
        form.setFieldsValue({
          memberAccount: record.memberAccount,
          restrictionTypes: record.restrictionTypes,
          activityIds: record.activityIds,
          remarks: record.remarks,
        });
      } else {
        const fieldName = activeTab === 'ip' ? 'ipAddress' :
                         activeTab === 'phone' ? 'phoneNumber' :
                         activeTab === 'email' ? 'email' :
                         activeTab === 'device' ? 'device' : 'paymentAccount';
        form.setFieldsValue({
          [fieldName]: record[fieldName],
          restrictionType: record.restrictionType,
          remarks: record.remarks,
        });
      }
    } else {
      setEditingRecord(null);
      form.resetFields();
      if (activeTab === 'member') {
        form.setFieldsValue({
          restrictionTypes: [],
        });
      }
    }
    setModalVisible(true);
  };

  // 处理保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (activeTab === 'member') {
        const newRecord: MemberBlacklist = {
          id: editingRecord ? editingRecord.id : Date.now(),
          memberId: editingRecord ? editingRecord.memberId : Math.floor(Math.random() * 1000000) + 258714,
          memberAccount: values.memberAccount,
          restrictionTypes: values.restrictionTypes,
          activityIds: values.restrictionTypes.includes('reward') ? values.activityIds : undefined,
          activityNames: values.restrictionTypes.includes('reward') && values.activityIds
            ? mockActivities.filter(a => values.activityIds.includes(a.id)).map(a => a.name)
            : undefined,
          remarks: values.remarks,
          operator: 'admin',
          createTime: editingRecord ? editingRecord.createTime : dayjs().format('YYYY-MM-DD HH:mm:ss'),
          updateTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        };

        if (editingRecord) {
          setMemberBlacklistData(prev => prev.map(item => (item.id === editingRecord.id ? newRecord : item)));
          message.success('黑名单已更新');
        } else {
          setMemberBlacklistData(prev => [newRecord, ...prev]);
          message.success('黑名单已添加');
        }
      } else {
        const fieldName = activeTab === 'ip' ? 'ipAddress' :
                         activeTab === 'phone' ? 'phoneNumber' :
                         activeTab === 'email' ? 'email' :
                         activeTab === 'device' ? 'device' : 'paymentAccount';
        
        const newRecord: any = {
          id: editingRecord ? editingRecord.id : Date.now(),
          [fieldName]: values[fieldName],
          restrictionType: values.restrictionType,
          remarks: values.remarks,
          operator: 'admin',
          createTime: editingRecord ? editingRecord.createTime : dayjs().format('YYYY-MM-DD HH:mm:ss'),
          updateTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        };

        if (activeTab === 'ip') {
          if (editingRecord) {
            setIpBlacklistData(prev => prev.map(item => (item.id === editingRecord.id ? newRecord : item)));
          } else {
            setIpBlacklistData(prev => [newRecord, ...prev]);
          }
        } else if (activeTab === 'phone') {
          if (editingRecord) {
            setPhoneBlacklistData(prev => prev.map(item => (item.id === editingRecord.id ? newRecord : item)));
          } else {
            setPhoneBlacklistData(prev => [newRecord, ...prev]);
          }
        } else if (activeTab === 'email') {
          if (editingRecord) {
            setEmailBlacklistData(prev => prev.map(item => (item.id === editingRecord.id ? newRecord : item)));
          } else {
            setEmailBlacklistData(prev => [newRecord, ...prev]);
          }
        } else if (activeTab === 'device') {
          if (editingRecord) {
            setDeviceBlacklistData(prev => prev.map(item => (item.id === editingRecord.id ? newRecord : item)));
          } else {
            setDeviceBlacklistData(prev => [newRecord, ...prev]);
          }
        } else if (activeTab === 'payment') {
          if (editingRecord) {
            setPaymentAccountBlacklistData(prev => prev.map(item => (item.id === editingRecord.id ? newRecord : item)));
          } else {
            setPaymentAccountBlacklistData(prev => [newRecord, ...prev]);
          }
        }
        message.success(editingRecord ? '黑名单已更新' : '黑名单已添加');
      }

      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // 处理删除
  const handleDelete = (id: number) => {
    if (activeTab === 'member') {
      setMemberBlacklistData(prev => prev.filter(item => item.id !== id));
    } else if (activeTab === 'ip') {
      setIpBlacklistData(prev => prev.filter(item => item.id !== id));
    } else if (activeTab === 'phone') {
      setPhoneBlacklistData(prev => prev.filter(item => item.id !== id));
    } else if (activeTab === 'email') {
      setEmailBlacklistData(prev => prev.filter(item => item.id !== id));
    } else if (activeTab === 'device') {
      setDeviceBlacklistData(prev => prev.filter(item => item.id !== id));
    } else if (activeTab === 'payment') {
      setPaymentAccountBlacklistData(prev => prev.filter(item => item.id !== id));
    }
    message.success('黑名单已删除');
  };

  // 会员黑名单表格列
  const memberColumns = [
    {
      title: '会员ID',
      dataIndex: 'memberId',
      key: 'memberId',
      width: 120,
    },
    {
      title: '账号',
      dataIndex: 'memberAccount',
      key: 'memberAccount',
      width: 150,
    },
    {
      title: '充/提次数',
      dataIndex: 'depositWithdrawalCount',
      key: 'depositWithdrawalCount',
      width: 120,
      render: (text: string) => text || '/',
    },
    {
      title: '充/提金额',
      dataIndex: 'depositWithdrawalAmount',
      key: 'depositWithdrawalAmount',
      width: 150,
      render: (text: string) => text || '/',
    },
    {
      title: '充/提差额',
      dataIndex: 'depositWithdrawalDifference',
      key: 'depositWithdrawalDifference',
      width: 120,
      render: (num: number) => num?.toLocaleString() || '0',
    },
    {
      title: '总余额',
      dataIndex: 'totalBalance',
      key: 'totalBalance',
      width: 150,
      render: (num: number) => num?.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00',
    },
    {
      title: '注册IP',
      dataIndex: 'registrationIp',
      key: 'registrationIp',
      width: 150,
      render: (text: string) => text || '-',
    },
    {
      title: '限制类型',
      key: 'restrictionTypes',
      width: 200,
      render: (_: any, record: MemberBlacklist) => (
        <Space wrap>
          {record.restrictionTypes.map(type => {
            const option = restrictionTypeOptions.find(opt => opt.value === type);
            return option ? (
              <Tag key={type} color="red">{option.label}</Tag>
            ) : null;
          })}
        </Space>
      ),
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 200,
    },
    {
      title: '操作人',
      dataIndex: 'operator',
      key: 'operator',
      width: 100,
    },
    {
      title: '创建/更新时间',
      key: 'time',
      width: 180,
      render: (_: any, record: MemberBlacklist) => (
        <div>
          <div>{record.createTime}</div>
          {record.updateTime !== record.createTime && (
            <div style={{ color: '#999', fontSize: 12 }}>{record.updateTime}</div>
          )}
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: MemberBlacklist) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这条黑名单吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedMemberId(record.memberId);
              setDetailModalVisible(true);
            }}
          >
            拦截明细
          </Button>
        </Space>
      ),
    },
  ];

  // 通用表格列（IP、手机、邮箱、设备、支付账号）
  const getCommonColumns = (fieldName: string, fieldLabel: string) => [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: fieldLabel,
      dataIndex: fieldName,
      key: fieldName,
      width: 200,
    },
    {
      title: '限制类型',
      dataIndex: 'restrictionType',
      key: 'restrictionType',
      width: 120,
      render: (type: string) => {
        const option = restrictionTypeOptions.find(opt => opt.value === type);
        return option ? <Tag color="red">{option.label}</Tag> : type;
      },
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 200,
    },
    {
      title: '操作人',
      dataIndex: 'operator',
      key: 'operator',
      width: 100,
    },
    {
      title: '创建/更新时间',
      key: 'time',
      width: 180,
      render: (_: any, record: any) => (
        <div>
          <div>{record.createTime}</div>
          {record.updateTime !== record.createTime && (
            <div style={{ color: '#999', fontSize: 12 }}>{record.updateTime}</div>
          )}
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这条黑名单吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 拦截明细表格列
  const interceptDetailColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 100,
    },
    {
      title: '用户账号',
      dataIndex: 'userAccount',
      key: 'userAccount',
      width: 150,
    },
    {
      title: '拦截类型',
      dataIndex: 'interceptTypeName',
      key: 'interceptTypeName',
      width: 120,
      render: (name: string, record: InterceptDetail) => {
        const colorMap: Record<string, string> = {
          '奖励派发': 'orange',
          '进游戏': 'blue',
          '提现': 'red',
          '登录': 'purple',
          '注册': 'cyan',
          '充值': 'green',
        };
        return <Tag color={colorMap[name] || 'default'}>{name}</Tag>;
      },
    },
    {
      title: '活动名称',
      dataIndex: 'activityName',
      key: 'activityName',
      width: 150,
      render: (name: string | undefined) => name || '-',
    },
    {
      title: '拦截原因',
      dataIndex: 'reason',
      key: 'reason',
      width: 250,
    },
    {
      title: '拦截时间',
      dataIndex: 'interceptTime',
      key: 'interceptTime',
      width: 180,
    },
  ];

  // 获取搜索字段标签
  const getSearchFieldLabel = () => {
    switch (activeTab) {
      case 'member':
        return '会员账号';
      case 'ip':
        return 'IP地址';
      case 'phone':
        return '手机号码';
      case 'email':
        return '电子邮箱';
      case 'device':
        return '设备';
      case 'payment':
        return '支付账号';
      default:
        return '';
    }
  };

  // 获取搜索字段占位符
  const getSearchPlaceholder = () => {
    switch (activeTab) {
      case 'member':
        return '请输入用户名';
      case 'ip':
        return '请输入IP地址';
      case 'phone':
        return '请输入手机号码';
      case 'email':
        return '请输入电子邮箱';
      case 'device':
        return '请输入设备';
      case 'payment':
        return '请输入支付账号';
      default:
        return '';
    }
  };

  // 获取表格列
  const getColumns = () => {
    switch (activeTab) {
      case 'member':
        return memberColumns;
      case 'ip':
        return getCommonColumns('ipAddress', 'IP地址');
      case 'phone':
        return getCommonColumns('phoneNumber', '手机号码');
      case 'email':
        return getCommonColumns('email', '电子邮箱');
      case 'device':
        return getCommonColumns('device', '设备');
      case 'payment':
        return getCommonColumns('paymentAccount', '支付账号');
      default:
        return [];
    }
  };

  const filteredData = getFilteredData();

  return (
    <div style={{ padding: 24 }}>
      <div className="page-header">
        <h1>黑名单管理</h1>
        <p>管理各类黑名单，包括会员、IP、手机、邮箱、设备、支付账号等</p>
      </div>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
          <TabPane tab="会员黑名单" key="member">
            <Space style={{ marginBottom: 16, width: '100%' }} direction="vertical" size="middle">
              <Row gutter={16}>
                <Col span={6}>
                  <Input
                    placeholder={getSearchPlaceholder()}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Col>
                <Col span={6}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="限制类型"
                    value={filterType}
                    onChange={setFilterType}
                    allowClear
                  >
                    <Option value="all">全部</Option>
                    {restrictionTypeOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={12}>
                  <Space>
                    <Button type="primary" icon={<SearchOutlined />}>
                      搜索
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={() => {
                      setSearchText('');
                      setFilterType('all');
                    }}>
                      重置
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
                      新增
                    </Button>
                    <Button>批量修改</Button>
                    <Button danger>批量删除</Button>
                  </Space>
                </Col>
              </Row>
            </Space>

            <Table
              rowKey="id"
              dataSource={filteredData}
              columns={getColumns()}
              pagination={{
                total: filteredData.length,
                pageSize: 20,
                showTotal: (total) => `共 ${total} 条`,
                showSizeChanger: true,
                showQuickJumper: true,
              }}
              scroll={{ x: 1800 }}
            />
          </TabPane>

          <TabPane tab="IP黑名单" key="ip">
            <Space style={{ marginBottom: 16, width: '100%' }} direction="vertical" size="middle">
              <Row gutter={16}>
                <Col span={6}>
                  <Input
                    placeholder={getSearchPlaceholder()}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Col>
                <Col span={6}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="限制类型"
                    value={filterType}
                    onChange={setFilterType}
                    allowClear
                  >
                    <Option value="all">全部</Option>
                    {restrictionTypeOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={12}>
                  <Space>
                    <Button type="primary" icon={<SearchOutlined />}>
                      搜索
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={() => {
                      setSearchText('');
                      setFilterType('all');
                    }}>
                      重置
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
                      新增
                    </Button>
                    <Button>批量修改</Button>
                    <Button danger>批量删除</Button>
                  </Space>
                </Col>
              </Row>
            </Space>

            <Table
              rowKey="id"
              dataSource={filteredData}
              columns={getColumns()}
              pagination={{
                total: filteredData.length,
                pageSize: 20,
                showTotal: (total) => `共 ${total} 条`,
                showSizeChanger: true,
                showQuickJumper: true,
              }}
              scroll={{ x: 1200 }}
            />
          </TabPane>

          <TabPane tab="手机黑名单" key="phone">
            <Space style={{ marginBottom: 16, width: '100%' }} direction="vertical" size="middle">
              <Row gutter={16}>
                <Col span={6}>
                  <Input
                    placeholder={getSearchPlaceholder()}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Col>
                <Col span={6}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="限制类型"
                    value={filterType}
                    onChange={setFilterType}
                    allowClear
                  >
                    <Option value="all">全部</Option>
                    {restrictionTypeOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={12}>
                  <Space>
                    <Button type="primary" icon={<SearchOutlined />}>
                      搜索
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={() => {
                      setSearchText('');
                      setFilterType('all');
                    }}>
                      重置
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
                      新增
                    </Button>
                    <Button>批量修改</Button>
                    <Button danger>批量删除</Button>
                  </Space>
                </Col>
              </Row>
            </Space>

            <Table
              rowKey="id"
              dataSource={filteredData}
              columns={getColumns()}
              pagination={{
                total: filteredData.length,
                pageSize: 20,
                showTotal: (total) => `共 ${total} 条`,
                showSizeChanger: true,
                showQuickJumper: true,
              }}
              scroll={{ x: 1200 }}
            />
          </TabPane>

          <TabPane tab="邮箱黑名单" key="email">
            <Space style={{ marginBottom: 16, width: '100%' }} direction="vertical" size="middle">
              <Row gutter={16}>
                <Col span={6}>
                  <Input
                    placeholder={getSearchPlaceholder()}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Col>
                <Col span={6}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="限制类型"
                    value={filterType}
                    onChange={setFilterType}
                    allowClear
                  >
                    <Option value="all">全部</Option>
                    {restrictionTypeOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={12}>
                  <Space>
                    <Button type="primary" icon={<SearchOutlined />}>
                      搜索
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={() => {
                      setSearchText('');
                      setFilterType('all');
                    }}>
                      重置
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
                      新增
                    </Button>
                    <Button>批量修改</Button>
                    <Button danger>批量删除</Button>
                  </Space>
                </Col>
              </Row>
            </Space>

            <Table
              rowKey="id"
              dataSource={filteredData}
              columns={getColumns()}
              pagination={{
                total: filteredData.length,
                pageSize: 20,
                showTotal: (total) => `共 ${total} 条`,
                showSizeChanger: true,
                showQuickJumper: true,
              }}
              scroll={{ x: 1200 }}
            />
          </TabPane>

          <TabPane tab="设备" key="device">
            <Space style={{ marginBottom: 16, width: '100%' }} direction="vertical" size="middle">
              <Row gutter={16}>
                <Col span={6}>
                  <Input
                    placeholder={getSearchPlaceholder()}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Col>
                <Col span={6}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="限制类型"
                    value={filterType}
                    onChange={setFilterType}
                    allowClear
                  >
                    <Option value="all">全部</Option>
                    {restrictionTypeOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={12}>
                  <Space>
                    <Button type="primary" icon={<SearchOutlined />}>
                      搜索
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={() => {
                      setSearchText('');
                      setFilterType('all');
                    }}>
                      重置
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
                      新增
                    </Button>
                    <Button>批量修改</Button>
                    <Button danger>批量删除</Button>
                  </Space>
                </Col>
              </Row>
            </Space>

            <Table
              rowKey="id"
              dataSource={filteredData}
              columns={getColumns()}
              pagination={{
                total: filteredData.length,
                pageSize: 20,
                showTotal: (total) => `共 ${total} 条`,
                showSizeChanger: true,
                showQuickJumper: true,
              }}
              scroll={{ x: 1200 }}
            />
          </TabPane>

          <TabPane tab="支付账号" key="payment">
            <Space style={{ marginBottom: 16, width: '100%' }} direction="vertical" size="middle">
              <Row gutter={16}>
                <Col span={6}>
                  <Input
                    placeholder={getSearchPlaceholder()}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Col>
                <Col span={6}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="限制类型"
                    value={filterType}
                    onChange={setFilterType}
                    allowClear
                  >
                    <Option value="all">全部</Option>
                    {restrictionTypeOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={12}>
                  <Space>
                    <Button type="primary" icon={<SearchOutlined />}>
                      搜索
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={() => {
                      setSearchText('');
                      setFilterType('all');
                    }}>
                      重置
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
                      新增
                    </Button>
                    <Button>批量修改</Button>
                    <Button danger>批量删除</Button>
                  </Space>
                </Col>
              </Row>
            </Space>

            <Table
              rowKey="id"
              dataSource={filteredData}
              columns={getColumns()}
              pagination={{
                total: filteredData.length,
                pageSize: 20,
                showTotal: (total) => `共 ${total} 条`,
                showSizeChanger: true,
                showQuickJumper: true,
              }}
              scroll={{ x: 1200 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingRecord ? '编辑黑名单' : `新增${activeTab === 'member' ? '用户' : activeTab === 'ip' ? 'IP' : activeTab === 'phone' ? '手机' : activeTab === 'email' ? '邮箱' : activeTab === 'device' ? '设备' : '支付账号'}黑名单`}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={handleSave}
        width={activeTab === 'member' ? 800 : 600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
        >
          {activeTab === 'member' ? (
            <>
              <Form.Item
                label="会员账号"
                name="memberAccount"
                rules={[{ required: true, message: '请输入会员账号' }]}
              >
                <Input
                  placeholder="请填写会员账号"
                  suffix={
                    <Button type="link" size="small">
                      查询
                    </Button>
                  }
                />
              </Form.Item>

              <Form.Item
                label="限制类型"
                name="restrictionTypes"
                rules={[{ required: true, message: '请至少选择一种限制类型' }]}
              >
                <Checkbox.Group>
                  <Row gutter={16}>
                    {restrictionTypeOptions.map(opt => (
                      <Col span={8} key={opt.value}>
                        <Checkbox value={opt.value}>{opt.label}</Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.restrictionTypes !== currentValues.restrictionTypes
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue('restrictionTypes')?.includes('reward') ? (
                    <Form.Item
                      label="选择活动"
                      name="activityIds"
                      rules={[{ required: true, message: '请至少选择一个活动' }]}
                      extra="被加入黑名单的用户无法参加这些活动（不论自动派发奖励）"
                    >
                      <Select
                        mode="multiple"
                        placeholder="请选择活动"
                        showSearch
                        optionFilterProp="children"
                      >
                        {mockActivities.map(activity => (
                          <Option key={activity.id} value={activity.id}>
                            {activity.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ) : null
                }
              </Form.Item>

              <Form.Item
                label="备注"
                name="remarks"
                rules={[{ required: true, message: '请输入备注' }]}
              >
                <TextArea
                  placeholder="请填写备注"
                  rows={4}
                />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                label={getSearchFieldLabel()}
                name={activeTab === 'ip' ? 'ipAddress' :
                     activeTab === 'phone' ? 'phoneNumber' :
                     activeTab === 'email' ? 'email' :
                     activeTab === 'device' ? 'device' : 'paymentAccount'}
                rules={[{ required: true, message: `请输入${getSearchFieldLabel()}` }]}
              >
                <Input placeholder={`请输入${getSearchFieldLabel()}`} />
              </Form.Item>

              <Form.Item
                label="限制类型"
                name="restrictionType"
                rules={[{ required: true, message: '请选择限制类型' }]}
              >
                <Select placeholder="请选择限制类型">
                  {restrictionTypeOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="备注"
                name="remarks"
                rules={[{ required: true, message: '请输入备注' }]}
              >
                <TextArea
                  placeholder="请填写备注"
                  rows={4}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* 拦截明细弹窗 */}
      <Modal
        title="拦截明细"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedMemberId(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setDetailModalVisible(false);
            setSelectedMemberId(null);
          }}>
            关闭
          </Button>,
        ]}
        width={1200}
      >
        <Table
          rowKey="id"
          dataSource={selectedMemberId
            ? interceptDetails.filter(d => d.userId === selectedMemberId)
            : interceptDetails}
          columns={interceptDetailColumns}
          pagination={{
            total: selectedMemberId
              ? interceptDetails.filter(d => d.userId === selectedMemberId).length
              : interceptDetails.length,
            pageSize: 10,
            showTotal: (total) => `共 ${total} 条`,
          }}
          scroll={{ x: 1000 }}
        />
      </Modal>
    </div>
  );
};

export default BlacklistManagement;

